// spell-display.ts — shared single-source renderers for a spell/ability's DURATION, RANGE, and AOE,
// used by the PC-sheet Castable / Prepare / Mentas tabs and (for duration) the side-info property
// list. Sibling to spell-casting-time.ts.
//
// IMPORTANT — two legitimate forms, not one. These dimensions render in TWO shapes, each matching a
// different PHP twin, so we do NOT collapse them (that would break item.php ↔ openSideInfo parity):
//   • the SIDE-INFO / detail form (rich AOE, full duration labels) ↔ list_display_range_aoe_inc.php
//   • the COMPACT spell-table form (icon-only range, icon+distance AOE, short duration labels)
//     ↔ fate_tide_spell_row_supporting_columns_html()
// What we consolidate is the SOURCE within each form: the three PC-sheet tabs had drifted triplicate
// copies (the mentas duration map was missing 'rounds' and singular 'hr'; prepare-spells used a
// leading-space/capitalized variant) — those now share one implementation here.
//
// iconMap/escapeHtml are runtime globals reached via globalThis (matches shared/render-table-tag.ts).

import { layerOf, layerHas, scalarLayerSlot } from './array-types';

const _g: any = globalThis as any;
function icon(key: string): string {
    return typeof _g.iconMap === 'function' ? _g.iconMap(key) : '';
}
function esc(s: unknown): string {
    if (typeof _g.escapeHtml === 'function') return _g.escapeHtml(String(s ?? ''));
    const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s ?? '').replace(/[&<>"']/g, m => map[m] ?? m);
}

// ── DURATION ────────────────────────────────────────────────────────────────────
// Canonical singular/plural unit labels, FULL ("8 Hours") + SHORT ("8 hrs") forms.
// [0] = singular (n === 1), [1] = plural. Single source for both PHP twins + every TS surface;
// tools/test/spell-casting-time-parity.mjs guards these against the PHP get_spell_duration_display().
export const DURATION_LABELS_FULL: Record<string, [string, string]> = {
    seconds: ['Second', 'Seconds'],
    hours: ['Hour', 'Hours'],
    minutes: ['Minute', 'Minutes'],
    days: ['Day', 'Days'],
    weeks: ['Week', 'Weeks'],
    months: ['Month', 'Months'],
    years: ['Year', 'Years'],
    rounds: ['Round', 'Rounds'],
};
export const DURATION_LABELS_SHORT: Record<string, [string, string]> = {
    seconds: ['sec', 'secs'],
    hours: ['hr', 'hrs'],
    minutes: ['min', 'mins'],
    days: ['day', 'days'],
    weeks: ['week', 'weeks'],
    months: ['month', 'months'],
    years: ['year', 'years'],
    rounds: ['round', 'rounds'],
};
export function durationUnitLabel(type: string, n: number, short = false): string {
    const m = (short ? DURATION_LABELS_SHORT : DURATION_LABELS_FULL)[type];
    if (!m) return '';
    return n === 1 ? m[0] : m[1];
}

// Duration VALUE fragment (no label/concentration wrapper — the caller keeps its own concentration
// placement): "Instant" for a zero int with no type, the permanent icon for type==='permanent', else
// "<int> <unit>". '' when absent. `short` picks the compact spell-table form.
export function displayDuration(arr: any, short = false): string {
    const hasInt = layerHas(arr, 'duration_int');
    const hasType = layerHas(arr, 'duration_type');
    const dt = String(layerOf<string>(arr, 'duration_type') ?? '');
    const diRaw = layerOf(arr, 'duration_int');
    if (hasInt && !hasType) {
        return Number(scalarLayerSlot(diRaw ?? '')) === 0 ? 'Instant' : '';
    }
    if (dt === 'permanent') return icon('duration_permanent');
    if (hasInt && hasType) {
        const n = Number(scalarLayerSlot(diRaw ?? ''));
        const lbl = durationUnitLabel(dt, n, short);
        return `${esc(scalarLayerSlot(diRaw ?? ''))}${lbl ? ' ' + lbl : ''}`;
    }
    return '';
}

// ── COMPACT spell-table forms (PC-sheet Castable / Prepare / Mentas) ───────────────
// range: the range_type icon (icon-only — the side-info form makes it clickable, this one doesn't,
// matching the spell-row PHP). '' when absent.
export function rangeIcon(arr: any): string {
    const r = String(layerOf<string>(arr, 'range') ?? '');
    return r ? icon(r) : '';
}
// aoe: the type icon over a distance badge. '' when no aoe_type (callers render '—').
export function displayAoeCompact(arr: any): string {
    if (!layerHas(arr, 'aoe_type')) return '';
    const t = String(layerOf<string>(arr, 'aoe_type') ?? '');
    return `${icon(t)}<br /><span class='badge bg-dark border'>${esc(scalarLayerSlot(layerOf(arr, 'aoe_distance') ?? ''))}</span>`;
}

// ── CONSUMED / ADDED CHARGE ability icon ───────────────────────────────────────────────────────────
// The consumes_ability_charges / add_charges_to_ability layers hold a bare catalog-item UUID — an
// elemental "Water Mote"-style passive ability whose charge the spell spends (red) or grants (green).
// That UUID is NOT in ICON_LOOKUP, so iconMap(uuid) lands on the "?" help-circle (the reported bug).
// The bespoke CDN art for these abilities is keyed by the snake_cased item_name — the SAME resolution
// the rune-icon satellite charge glyph already uses (rune-icon-engine.ts artNode/iconFile), which is
// why column 1's rune shows the mote but this cell didn't. Resolve uuid → item_name → file → CDN here.
// Falls back to iconMap(uuid) (the "?") only when the name can't be resolved from the session catalog.
export function chargeAbilityIcon(uuid: unknown, w = 25, h = 25): string {
    const id = String(uuid ?? '');
    if (id === '') return '';
    const name = (typeof _g.ftItemNameById === 'function') ? String(_g.ftItemNameById(id) ?? '') : '';
    if (name === '') return icon(id); // unresolved → iconMap's "?" (UUID tooltip), behaviour unchanged
    // Name-keyed CDN art: a registered icon wins (rare for these abilities), else snake_case the name
    // (matches rune-icon-engine.ts iconFile/vname).
    const reg = (_g.FT_ICON_REGISTRY || _g.ICON_LOOKUP || {}) as Record<string, { file?: string }>;
    const regEntry = reg[name] || reg[name.toLowerCase()];
    const file = (regEntry && regEntry.file) ? regEntry.file : name.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const src = `https://cdn.fatetide.com/images/icons/${file}.png`;
    const e = esc(name);
    // Mirror iconMap()'s single-label span: tooltip + press-hold (data-ft-info-item-id) on the UUID.
    return `<span class="d-inline-block ft-icon-map" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${e}" data-ft-info-item-id="${esc(id)}"><img src="${src}" width="${w}" height="${h}" alt="${e}" onerror="this.src='https://cdn.fatetide.com/images/icons/help-circle-outline.png'; this.onerror=null;"></span>`;
}

// ── SPELL LEVEL badge — the bg-dark form (side-info "Spell Level:" row + list rows). The object-card
// HEADER intentionally uses a different bg-primary badge to match table_item_object_inc.php, so it is
// NOT routed through here. ────────────────────────────────────────────────────────────────────────
export function displaySpellLevelBadge(arr: any): string {
    return `<span class="badge bg-dark border border-light">${esc(scalarLayerSlot(layerOf(arr, 'spell_level') ?? ''))}</span>`;
}
